import { supabase } from './client';
import { Database } from '../../types/supabase';

export type Routine = Database['public']['Tables']['routines']['Row'];
export type Task = Database['public']['Tables']['tasks']['Row'];
export type PredefinedRoutine = Database['public']['Tables']['predefined_routines']['Row'];
export type PredefinedRoutineTask = Database['public']['Tables']['predefined_routine_tasks']['Row'];

export type RoutineWithTasks = Routine & { tasks: Task[] };

export const routinesService = {
    async getPredefinedRoutines() {
        const { data, error } = await supabase
            .from('predefined_routines')
            .select(`
                *,
                tasks:predefined_routine_tasks(*)
            `)
            .order('default_duration_minutes', { ascending: true });
        
        if (error) throw error;
        return data;
    },

    async getRoutinesForProfile(profileId: string): Promise<RoutineWithTasks[]> {
        // Use explicit relationship hint to avoid PGRST201 error
        const { data, error } = await supabase
            .from('routines')
            .select(`
                *,
                tasks!tasks_routine_id_fkey(*)
            `)
            .eq('profile_id', profileId)
            .order('created_at', { ascending: false });
            
        if (error) throw error;
        return data as RoutineWithTasks[];
    },

    // Fetch routines by their IDs (used as fallback when profile lookup fails)
    async getRoutinesByIds(routineIds: string[]): Promise<RoutineWithTasks[]> {
        if (routineIds.length === 0) return [];
        
        // Use explicit relationship hint to avoid PGRST201 error
        const { data, error } = await supabase
            .from('routines')
            .select(`
                *,
                tasks!tasks_routine_id_fkey(*)
            `)
            .in('id', routineIds)
            .order('created_at', { ascending: false });
            
        if (error) {
            console.error('[getRoutinesByIds] Error:', error);
            throw error;
        }
        
        return data as RoutineWithTasks[];
    },

    async getDayPlan(profileId: string, date: string): Promise<Task[]> {
        const { data, error } = await supabase
            .rpc('get_day_plan', {
                p_profile_id: profileId,
                p_date: date
            });
            
        if (error) throw error;
        return data || [];
    },

    async createRoutine(params: {
        childIds: string[];
        name: string;
        type: string;
        startTime: string; // HH:MM
        endTime: string;   // HH:MM
        daysOfWeek: number[];
        startDate: string;
        endDate: string;
        color?: string;
        tasks: Array<{
            label: string;
            icon: string;
            color: string;
            start_time: string; // HH:MM
            end_time: string;   // HH:MM
            order_index: number;
        }>;
    }) {
        // Validate that we have at least one child ID
        if (!params.childIds || params.childIds.length === 0) {
            throw new Error('At least one child profile must be selected to create a routine');
        }

        console.log('[createRoutine] Creating routine with:', {
            childIds: params.childIds,
            name: params.name,
            tasksCount: params.tasks.length
        });

        // historical: the p_is_vacation parameter was removed from the RPC
        // in migration g4h5i6j7k8l9. The auto-generated src/types/supabase.ts
        // still reflects the old signature until regenerated from the
        // Supabase dashboard, so we cast through `any` here to keep TS happy
        // without touching that file.
        const { error } = await (supabase.rpc as any)('create_routine_with_tasks', {
            p_child_ids: params.childIds,
            p_name: params.name,
            p_type: params.type,
            p_start_time: params.startTime,
            p_end_time: params.endTime,
            p_days_of_week: params.daysOfWeek,
            p_start_date: params.startDate,
            p_end_date: params.endDate,
            p_color: params.color || null,
            p_tasks: params.tasks
        });

        if (error) {
            console.error('[createRoutine] RPC error:', error);
            throw error;
        }
        
        console.log('[createRoutine] Routine created successfully');
    },

    async updateRoutine(routineId: string, updates: {
        name?: string;
        type?: string;
        startTime?: string;
        endTime?: string;
        daysOfWeek?: number[];
        startDate?: string;
        endDate?: string;
        isActive?: boolean;
        color?: string;
    }) {
        if (!routineId) {
            throw new Error('Routine ID is required');
        }

        const dbUpdates: Record<string, unknown> = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.type !== undefined) dbUpdates.type = updates.type;
        if (updates.startTime !== undefined) dbUpdates.start_time = updates.startTime;
        if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;
        if (updates.daysOfWeek !== undefined) dbUpdates.days_of_week = updates.daysOfWeek;
        if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
        if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
        if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
        if (updates.color !== undefined) dbUpdates.color = updates.color;

        const { data, error } = await supabase
            .from('routines')
            .update(dbUpdates)
            .eq('id', routineId)
            .select()
            .single();
            
        if (error) {
            console.error('[updateRoutine] Failed to update routine:', error);
            throw error;
        }
        
        return data;
    },

    async deleteRoutine(routineId: string) {
        if (!routineId) {
            throw new Error('Routine ID is required');
        }

        // Validate that routineId looks like a UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(routineId)) {
            throw new Error(`Invalid routine ID: ${routineId}`);
        }

        console.log('[deleteRoutine] Starting deletion for routine:', routineId);

        // First, get all task IDs for this routine
        const { data: tasks, error: fetchError } = await supabase
            .from('tasks')
            .select('id')
            .eq('routine_id', routineId);
        
        if (fetchError) {
            console.error('[deleteRoutine] Failed to fetch tasks:', fetchError);
            throw fetchError;
        }
        
        console.log('[deleteRoutine] Found tasks:', tasks?.length || 0);

        if (tasks && tasks.length > 0) {
            const { error: tasksDeleteError } = await supabase
                .from('tasks')
                .delete()
                .eq('routine_id', routineId);

            if (tasksDeleteError) {
                console.error('[deleteRoutine] Failed to delete tasks:', tasksDeleteError);
                throw tasksDeleteError;
            }

            console.log('[deleteRoutine] Tasks deleted');
        }

        // Now delete the routine
        console.log('[deleteRoutine] Deleting routine:', routineId);
        const { data, error } = await supabase
            .from('routines')
            .delete()
            .eq('id', routineId)
            .select();
            
        if (error) {
            console.error('[deleteRoutine] Failed to delete routine:', error);
            throw error;
        }
        
        // Check if any rows were actually deleted
        if (!data || data.length === 0) {
            console.warn('[deleteRoutine] No routine found with ID:', routineId);
        }
        
        console.log('[deleteRoutine] Routine deleted successfully');
    }
};
